import { useMutation } from '@tanstack/react-query';
import { clientService, CreateClientPayload } from '@services/client';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  getErrorMessage,
  isClientEmailAlreadyMemberError,
  isClientEmailAlreadyLinkedError,
} from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

export const useCreateClient = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  const tClients = useTranslation('clients');

  return useMutation({
    mutationKey: ['clients'],
    mutationFn: async (payload: CreateClientPayload) => {
      return await clientService.createClient(payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Sprint 25, story-128 (run 5). `createClient` invites on the same round trip when the
      // Client was created with an email (Flow A). The API doesn't distinguish "brand-new
      // Cognito user" from "linked an existing login" (both land as `linkedUser.status ===
      // 'INVITED'`), so both share one toast; a client created with no email keeps the
      // pre-existing generic message.
      if (data?.email && data?.linkedUser) {
        toast.success(
          (tClients['createdInvited'] || 'Client created. Invitation sent to {email}.').replace(
            '{email}',
            data.email,
          ),
        );
      } else if (data?.email && !data?.linkedUser) {
        // The invite failed non-fatally (e.g. a transient Cognito error) — `ClientService.create`
        // deliberately does not roll back the Client row for this case. Surface it so an admin
        // knows to retry from the Client access tab instead of assuming the invite went out.
        toast.warning(
          tActionMessages.clientCreatedInvitationFailed,
        );
      } else {
        toast.success(tActionMessages.clientCreatedSuccessfully);
      }
    },
    onError: (error: any) => {
      // Sprint 25, story-128 (run 5). The two "refuse-whole-create" cases are shown inline under
      // Email by CreateEditClientModal instead of a toast — skip the generic toast for those so
      // the user isn't told twice.
      if (isClientEmailAlreadyMemberError(error) || isClientEmailAlreadyLinkedError(error)) {
        return;
      }
      toast.error(getErrorMessage(error, tActionMessages.requestError));
    },
  });
};

export default useCreateClient;
